import { create } from "zustand"
import type {
  CharacterGender,
  CharacterPersonality,
  CharacterStyle,
} from "@/shared/types/character"

export interface CharacterDraft {
  name: string
  /** How the character describes themselves (mandatory, shown at top like Character.AI) */
  description: string
  gender: CharacterGender | ""
  age: number
  ethnicity: string
  hairStyle: string
  hairColor: string
  eyeColor: string
  bodyType: string
  breastSize: string
  buttSize: string
  personality: CharacterPersonality[]
  /** Long-form definition; high impact on responses (optional, up to 32k chars) */
  backstory: string
  interests: string[]
  style: CharacterStyle | ""
  communicationStyle: string
  dominanceLevel: number
  // New personality/creation fields
  skinTone: string
  occupation: string
  relationshipType: string
  hobby: string
  fetish: string
  voiceId: string
  greeting: string
  scenario: string
  customPhysicalDetails: string
  additionalPersonalityDetails: string
  extraDetails: string
  /** Public = visible in explore; Private = only you */
  isPublic: boolean
}

const STEP_COUNT = 6

const initialDraft: CharacterDraft = {
  name: "",
  description: "",
  gender: "female",
  age: 25,
  ethnicity: "",
  hairStyle: "",
  hairColor: "",
  eyeColor: "",
  bodyType: "",
  breastSize: "",
  buttSize: "",
  personality: [],
  backstory: "",
  interests: [],
  style: "",
  communicationStyle: "",
  dominanceLevel: 50,
  skinTone: "",
  occupation: "",
  relationshipType: "",
  hobby: "",
  fetish: "",
  voiceId: "",
  greeting: "",
  scenario: "",
  customPhysicalDetails: "",
  additionalPersonalityDetails: "",
  extraDetails: "",
  isPublic: true,
}

interface CharacterStoreState {
  draft: CharacterDraft
  currentStep: number
  maxVisitedStep: number
  /** Bumped to remount the create wizard for a fresh character. */
  createWizardEpoch: number
  updateDraft: (updates: Partial<CharacterDraft>) => void
  setStep: (step: number) => void
  nextStep: () => void
  prevStep: () => void
  resetDraft: () => void
  restoreSession: (
    draft: CharacterDraft,
    currentStep: number,
    maxVisitedStep: number
  ) => void
  bumpCreateWizardEpoch: () => void
}

export const useCharacterStore = create<CharacterStoreState>((set) => ({
  draft: { ...initialDraft },
  currentStep: 0,
  maxVisitedStep: 0,
  createWizardEpoch: 0,

  updateDraft: (updates) =>
    set((state) => ({
      draft: { ...state.draft, ...updates },
    })),

  setStep: (step) =>
    set((state) => ({
      currentStep: step,
      maxVisitedStep: Math.max(state.maxVisitedStep, step),
    })),

  nextStep: () =>
    set((state) => ({
      currentStep: Math.min(state.currentStep + 1, STEP_COUNT - 1),
      maxVisitedStep: Math.max(state.maxVisitedStep, Math.min(state.currentStep + 1, STEP_COUNT - 1)),
    })),

  prevStep: () =>
    set((state) => ({
      currentStep: Math.max(state.currentStep - 1, 0),
    })),

  resetDraft: () =>
    set({
      draft: { ...initialDraft },
      currentStep: 0,
      maxVisitedStep: 0,
    }),

  restoreSession: (draft, currentStep, maxVisitedStep) =>
    set({
      draft: { ...initialDraft, ...draft },
      currentStep,
      maxVisitedStep,
    }),

  bumpCreateWizardEpoch: () =>
    set((state) => ({ createWizardEpoch: state.createWizardEpoch + 1 })),
}))
